import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ManagementApiService } from '../../../../../services';
import { MeasurementService } from '../../../../../services/measurement.service';
import { CalculationResult } from '../../../models/calculationModels';
import { CalculationType } from '../../../enums/calculation-type.enum';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CalculationSettingsComponent } from '../calculation-settings/calculation-settings.component';
import { UnitType } from 'src/app/models/enums/unit-type.enum';
import { Router } from '@angular/router';

export class StandardCalcResult {
    displayText: string;
    displayMetricValue: string | Number;
    displayImperialValue: string | Number;
}

@Component({
    selector: 'app-standard-output',
    templateUrl: './standard-output.component.html',
    styleUrls: ['./standard-output.component.scss'],
})
export class StandardOutputComponent implements OnChanges {
    @Input()
    set calcOutput(value: CalculationResult) {
        this._calcOutput = value;
        if (value != undefined && value != null) {
            let stdOutputs = this.mgmtSvc.userDefaults.find((x) => x.userPreferenceKey == 'CalculationOutputs').userPreference;
            this.loadStandardOutput(stdOutputs);
        }
    }

    get calcOutput(): CalculationResult {
        return this._calcOutput;
    }
    @Output() showAdvOptPanel: EventEmitter<true> = new EventEmitter();

    stdCalcResults: StandardCalcResult[];
    hideButton: boolean = true;
    private _calcOutput: CalculationResult;

    constructor(
        private ngbModal: NgbModal,
        private measSvc: MeasurementService,
        private mgmtSvc: ManagementApiService,
        private cd: ChangeDetectorRef,
        public router: Router
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (this.calcOutput?.output?.general?.warnings) {
            let res = this.calcOutput.output.general.warnings.some((w) => w.severity <= 1);
            if (res === true) {
                this.hideButton = false;
            }
        }
    }

    loadStandardOutput(stdOutputs: any) {
        this.stdCalcResults = [];

        for (let output of stdOutputs.split(',')) {
            let calObject: StandardCalcResult;
            switch (output) {
                case 'Bolt Quantity':
                    calObject = {
                        displayText: output,
                        displayMetricValue: null,
                        displayImperialValue: this.calcOutput?.output?.bolt?.boltQuantity?.toString(),
                    };
                    break;
                case 'Bolt Size':
                    calObject = {
                        displayText: output,
                        displayMetricValue: null,
                        displayImperialValue: this.calcOutput?.output?.bolt?.boltSize,
                    };
                    break;
                case 'Applied Bolt Load Stress Percentage':
                    if (this.calcOutput.input.others.tighteningMethod == 0) {
                        calObject = {
                            displayText: output,
                            displayMetricValue: null,
                            displayImperialValue: 'N/A',
                        };
                    } else {
                        calObject = {
                            displayText: output,
                            displayMetricValue: null,
                            displayImperialValue: this.measSvc.getFormattedPercentage(this.calcOutput?.output?.bolt?.appliedBoltYieldPercentage1),
                        };
                    }

                    break;
                case 'Bolt Load':
                    calObject = {
                        displayText: output,
                        displayMetricValue: this.calcOutput?.output?.bolt?.appliedBoltLoad1?.metric?.stringValue,
                        displayImperialValue: this.calcOutput?.output?.bolt?.appliedBoltLoad1?.imperial?.stringValue,
                    };
                    break;
                case 'Combined Weight':
                    calObject = {
                        displayText: output,
                        displayMetricValue: this.calcOutput?.output?.weight?.combinedWeight?.metric?.stringValue,
                        displayImperialValue: this.calcOutput?.output?.weight?.combinedWeight?.imperial?.stringValue,
                    };
                    break;
                case 'Extension':
                    let metric = this.measSvc.getDisplayedUnit(this.calcOutput?.output?.bolt?.boltExtension?.metric?.unit, UnitType.DefaultLengthUnit);
                    let imperial = this.measSvc.getDisplayedUnit(this.calcOutput?.output?.bolt?.boltExtension?.imperial?.unit, UnitType.DefaultLengthUnit);
                    calObject = {
                        displayText: output,
                        displayMetricValue: this.calcOutput?.output?.bolt?.boltExtension?.metric?.value.toFixed(5) + ' ' + metric,
                        displayImperialValue: this.calcOutput?.output?.bolt?.boltExtension?.imperial?.value.toFixed(5).toString() + imperial,
                    };
                    break;
                case 'Joint Thickness':
                    calObject = {
                        displayText: output,
                        displayMetricValue: this.calcOutput?.output?.flange?.jointThickness?.metric?.stringValue,
                        displayImperialValue: this.calcOutput?.output?.flange?.jointThickness?.imperial?.stringValue,
                    };
                    break;
                case 'Manufacturer Allows Clamp Alignment':
                    if (this.calcOutput.input.calculationType == CalculationType.Clamp) {
                        calObject = {
                            displayText: output,
                            displayMetricValue: null,
                            displayImperialValue: this.calcOutput?.output?.general?.manufacturerAllowsClampAlignment ? 'True' : 'False',
                        };
                    }
                    //For the StandardFlange calculation, do not display 'Manufacturer Allows Clamp Alignment'
                    // if (this.calcOutput?.input?.calculationType === CalculationType.StandardFlange) calObject.displayImperialValue = null;
                    break;

                case 'Minimum Bolt Length':
                    calObject = {
                        displayText: output,
                        displayMetricValue: this.calcOutput?.output?.flange?.minimumBoltLength?.metric?.stringValue,
                        displayImperialValue: this.calcOutput?.output?.flange?.minimumBoltLength?.imperial?.stringValue,
                    };
                    break;
                case 'Nut AF':
                    calObject = {
                        displayText: output,
                        displayMetricValue: this.calcOutput?.output?.bolt?.nutAF?.metric?.stringValue,
                        displayImperialValue: this.calcOutput?.output?.bolt?.nutAF?.imperial?.stringValue,
                    };
                    break;
                case 'Percentage Residual Yield':
                    calObject = {
                        displayText: output,
                        displayMetricValue: null,
                        displayImperialValue: this.measSvc.getFormattedPercentage(this.calcOutput?.output?.bolt?.residualBoltYieldPercentage),
                    };
                    break;
                case 'Residual Bolt Load':
                    calObject = {
                        displayText: output,
                        displayMetricValue: this.calcOutput?.output?.bolt?.residualBoltLoad?.metric?.stringValue,
                        displayImperialValue: this.calcOutput?.output?.bolt?.residualBoltLoad?.imperial?.stringValue,
                    };
                    break;
                case 'Residual Bolt Stress':
                    calObject = {
                        displayText: output,
                        displayMetricValue: this.calcOutput?.output?.bolt?.residualBoltStress?.metric?.stringValue,
                        displayImperialValue: this.calcOutput?.output?.bolt?.residualBoltStress?.imperial?.stringValue,
                    };
                    break;
                case 'Torque Long Form':
                    calObject = {
                        displayText: output,
                        displayMetricValue: this.calcOutput?.output?.general?.torqueLongForm?.metric?.stringValue,
                        displayImperialValue: this.calcOutput?.output?.general?.torqueLongForm?.imperial?.stringValue,
                    };
                    break;
                case 'Torque Short Form':
                    calObject = {
                        displayText: output,
                        displayMetricValue: this.calcOutput?.output?.general?.torqueShortForm?.metric?.stringValue,
                        displayImperialValue: this.calcOutput?.output?.general?.torqueShortForm?.imperial?.stringValue,
                    };
                    break;
                case 'Turn Of Nut':
                    calObject = {
                        displayText: output,
                        displayMetricValue: this.calcOutput?.output?.nut?.sigmaR?.metric?.stringValue,
                        displayImperialValue: this.calcOutput?.output?.nut?.sigmaR?.imperial?.stringValue,
                    };
                    break;
            }
            if (calObject?.displayMetricValue != null || calObject?.displayImperialValue != null) {
                this.stdCalcResults.push(calObject);
            }
        }
        this.cd.markForCheck();
    }

    showAdvancedOutputpanel() {
        this.showAdvOptPanel.emit(true);
    }

    openCalculationResultSettingsModal() {
        let dialogRef = this.ngbModal.open(CalculationSettingsComponent, {
            size: 'lg',
        });

        dialogRef.result
            .then((res: any) => {
                if (res != null || res != undefined) {
                    this.loadStandardOutput(res);
                    this.cd.markForCheck();
                }
            })
            .catch((e) => {
                console.warn(e);
            });
    }

    showAdvanced() {
        return !this.router.url.includes('idms/plantmanagerView') && this.hideButton == true && this.router.url != '/idms/plantmanager';
    }
}
